<template>
 <!-- Summary Cards Row -->
 <v-row dense>
      <v-col cols="12" sm="6" md="3">
        <v-skeleton-loader
          v-if="!dataLoaded"
          type="card"
          height="150"
        ></v-skeleton-loader>
        <TotalCardWithSelect
          v-else
          :items="[$t('totalItems'), $t('totalOpenItems')]"
          :count="totalItems" 
          :tooltip="showOpenItemsOnly ? $t('totalOpenItemsToolTip') : ''"          
        />
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-skeleton-loader
          v-if="!dataLoaded"
          type="card"
          height="150"
        ></v-skeleton-loader>
        <TotalCard
          v-else
          :label="$t('totalSubItems')"
          :count="totalSubItems"
          :tooltip="$t('totalSubItemsTooltip')"
        />  
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-skeleton-loader
          v-if="!dataLoaded"
          type="card"
          height="150"
        ></v-skeleton-loader>
        <TotalCardWithSwitch
          v-else
          identifier="totalAssignedToYourTeams"
          :label="$t('assignedToYourTeams')"
          :count="totalAssignedToYourTeams"
          @filter-change="handleFilterChange"
          v-model:selected="filterByAssignedToMyTeamsSelected"
        />
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-skeleton-loader
          v-if="!dataLoaded"
          type="card"
          height="150"
        ></v-skeleton-loader>
        <TotalCardWithSwitch
          v-else
          identifier="totalAssignedToYou"
          :label="$t('assignedToYou')"
          :count="totalAssignedToYou"
          @filter-change="handleFilterChange"
          v-model:selected="filterByAssignedToMeSelected"
        />
      </v-col>
    </v-row>
</template>



<script setup>
import { computed, watch, ref, onMounted } from 'vue';
import { useStore } from 'vuex';
import { debounce } from 'lodash';
import TotalCard from '../cards/TotalCard.vue';
import TotalCardWithSelect from '../cards/TotalCardWithSelect.vue';
import TotalCardWithSwitch from '../cards/TotalCardWithSwitch.vue';
import { getCurrentUserTeamIds, toSqlIdList } from '@/utils/userTeams';

const store = useStore();

const dataLoaded = computed(() => store.getters.getDataLoaded);
const conn = store.getters.getDBConnection;

const userId = computed(() => store.getters.getUserId);

const filterContext = computed(() => store.state.filterItemsContext);

const showOpenItemsOnly = computed(() => store.getters.getShowOpenItemsOnly);


const totalItems = ref(0);
const totalSubItems = ref(0);
const totalAssignedToYourTeams = ref(0);
const totalAssignedToYou = ref(0);

const filterByAssignedToMyTeamsSelected = ref(false);
const filterByAssignedToMeSelected = ref(false);

// Cache user's team IDs to avoid re-querying with lambda functions
const cachedUserTeamIds = ref([]);

const computeCounts = async () => {
  if (isMounted.value && dataLoaded.value) {
    try {
      const myTeamIds = await getCurrentUserTeamIds(conn, userId.value);
      cachedUserTeamIds.value = myTeamIds;
      const teamIdsList = toSqlIdList(myTeamIds);

      // Combined counts query - all from filteredItems
      // Use precise JSON array contains check to avoid partial matches (e.g., userId "1" matching "10")
      const combinedQuery = `
        SELECT
          COUNT(DISTINCT id) AS TotalItems,
          COUNT(subItemId) AS TotalSubItems,
          COUNT(CASE WHEN assigneeTeamId IN (${teamIdsList}) THEN subItemId END) AS AssignedToMyTeams,
          COUNT(CASE WHEN assigneeUserIds LIKE '%"${userId.value}"%' THEN subItemId END) AS AssignedToMe
        FROM filteredItems
      `;

      const result = await conn.query(combinedQuery);
      const row = result.toArray()[0];

      if (row) {
        const jsonRow = row.toJSON();
        totalItems.value = Number(jsonRow.TotalItems);
        totalSubItems.value = Number(jsonRow.TotalSubItems);
        totalAssignedToYourTeams.value = Number(jsonRow.AssignedToMyTeams);
        totalAssignedToYou.value = Number(jsonRow.AssignedToMe);
      }

    } catch (error) {
      console.error("Error executing counts query:", error);
      totalItems.value = 0;
      totalSubItems.value = 0;
      totalAssignedToYourTeams.value = 0;
      totalAssignedToYou.value = 0;
    }
  }
};

const handleFilterChange = async ({ id, selected }) => {
    if (id === 'totalAssignedToYourTeams') {
      await filterByAssignedToMyTeams(selected);
    } else if (id === 'totalAssignedToYou') {
      await filterByAssignedToMe(selected);
    }
  };

  const filterByAssignedToMyTeams = async (selected) => {
    const chartId = 'assignedToYourTeamsCardFilterContext';
    if (selected) {
      try {
        // If no cached IDs, try to fetch them now
        if (cachedUserTeamIds.value.length === 0) {
          try {
            const allTeamsQuery = `SELECT Id, Users FROM team_users`;
            const allTeamsResult = await conn.query(allTeamsQuery);
            const allTeams = allTeamsResult.toArray().map(row => row.toJSON());

            cachedUserTeamIds.value = allTeams
              .filter(team => {
                let users = team.Users;
                // Handle if Users is a string (JSON)
                if (typeof users === 'string') {
                  try {
                    users = JSON.parse(users);
                  } catch (e) {
                    return false;
                  }
                }
                // Ensure users is an array
                if (!Array.isArray(users)) {
                  return false;
                }
                return users.some(u => u.Id === userId.value && u.Current === true);
              })
              .map(team => team.Id);
          } catch (fetchError) {
            console.error('Error fetching team IDs:', fetchError);
          }
        }

        if (cachedUserTeamIds.value.length > 0) {
          const teamIdsList = cachedUserTeamIds.value.map(id => `'${id}'`).join(',');
          // Respect showOpenItemsOnly by joining with status_templates
          let query;
          if (showOpenItemsOnly.value) {
            query = `
              SELECT i.SubItemId
              FROM items i
              JOIN status_templates st ON i.StatusId = st.Id
              WHERE i.AssigneeTeamId IN (${teamIdsList})
                AND st.IsTerminal = FALSE
            `;
          } else {
            query = `SELECT SubItemId FROM items WHERE AssigneeTeamId IN (${teamIdsList})`;
          }
          await filterContext.value.updateFilterWithQuery(chartId, query);
        }
      } catch (error) {
        console.error('Error querying for team filter:', error);
      }
    } else {
      // Remove filter when untoggled
      await filterContext.value.removeFilter(chartId);
    }
  };

  const filterByAssignedToMe = async (selected) => {
    const chartId = 'assignedToMeCardFilterContext';
    if (selected) {
      // Respect showOpenItemsOnly by joining with status_templates
      let query;
      if (showOpenItemsOnly.value) {
        query = `
          SELECT i.SubItemId
          FROM items i
          JOIN status_templates st ON i.StatusId = st.Id
          WHERE i.AssigneeUserIds LIKE '%"${userId.value}"%'
            AND st.IsTerminal = FALSE
        `;
      } else {
        query = `SELECT SubItemId FROM items WHERE AssigneeUserIds LIKE '%"${userId.value}"%'`;
      }
      try {
        await filterContext.value.updateFilterWithQuery(chartId, query);
      } catch (error) {
        console.error('Error querying for user filter:', error);
      }
    } else {
      // Remove filter when untoggled
      await filterContext.value.removeFilter(chartId);
    }
  };



  const recomputeCounts = debounce(() => {
    computeCounts();
  }, 100);

  const isMounted = ref(false);

  watch(
      () => filterContext.value.filtersLastUpdated,
      () => {
        if (isMounted.value && dataLoaded.value) {
          recomputeCounts();
        }
      },
      { deep: true }
    );

  // Watch for dataLoaded to become true
  watch(
      dataLoaded,
      (newValue) => {
        if (newValue && isMounted.value) {
          recomputeCounts();
        }
      },
      { immediate: true }
    );

  onMounted(async () => {
    isMounted.value = true;
    if (dataLoaded.value) {
      recomputeCounts();
    }
  });


</script>
